import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import ProductGrid from '../components/product/ProductGrid';
import ProductFilters from '../components/product/ProductFilters';
import Pagination from '../components/ui/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, error, loading, fetchData } = useApi();
  
  // Get search parameters
  const searchQuery = searchParams.get('q') || '';
  const currentPage = Number(searchParams.get('page')) || 1;
  
  // Filter state management
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sort: searchParams.get('sort') || 'createdAt',
    order: searchParams.get('order') || 'desc'
  });

  // Fetch products when parameters change
  useEffect(() => {
    const fetchProducts = async () => {
      const params = {
        search: searchQuery,
        page: currentPage,
        limit: 12,
        ...filters
      };
      
      await fetchData('/api/products', params);
    };
    
    if (searchQuery) fetchProducts();
  }, [searchQuery, currentPage, filters]);

  // Update URL with new filters
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    navigate({
      search: `?q=${searchQuery}&${new URLSearchParams({
        ...newFilters,
        page: 1 // Reset to first page on filter change
      }).toString()}`
    }, { replace: true });
  };

  // Handle pagination
  const handlePageChange = (page) => {
    navigate({
      search: `?q=${searchQuery}&${new URLSearchParams({
        ...filters,
        page
      }).toString()}`
    }, { replace: true });
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (error) return <ErrorMessage message={error.message} fullScreen />;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Search Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Search Results for "{searchQuery}"
        </h1>
        {data?.pagination?.total && (
          <p className="text-gray-600">
            {data.pagination.total} {data.pagination.total === 1 ? 'result' : 'results'} found
          </p>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Filters Sidebar - Sticky on desktop */}
        <aside className="w-full md:w-64 flex-shrink-0">
          <div className="sticky top-4">
            <ProductFilters 
              filters={filters}
              onFilterChange={handleFilterChange}
              availableCategories={data?.filters?.categories || []}
              priceRange={data?.filters?.priceRange}
            />
          </div>
        </aside>

        {/* Results Section */}
        <main className="flex-1">
          {/* Results Grid */}
          {data?.products?.length > 0 ? (
            <>
              <ProductGrid products={data.products} className="mb-8" />
              <Pagination
                currentPage={currentPage}
                totalPages={data?.pagination?.pages || 1}
                onPageChange={handlePageChange}
                className="mt-8"
              />
            </>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
              <p className="text-gray-500">
                Try adjusting your search or filters to find what you're looking for.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SearchResults;